<template>
    <div :class="bannerClasses">
        <picture v-if="image || imageMobile" class="h-full w-full">
            <source v-if="imageMobile" :srcset="mobileImageUrl" media="(max-width: 1023px)" type="image/webp" />

            <NuxtImg
                :src="primaryImageUrl"
                :alt="image?.alt || imageMobile?.alt"
                rel="preload"
                @load="$emit('image-loaded')"
                v-bind="imageProps"
                class="h-full w-full object-cover" />
        </picture>
        <div :class="contentOverlayClasses">
            <div :class="textContainerClasses">
                <div>
                    <component :is="titleTag" v-if="title" :class="titleClasses">{{ titleWithBreak(title) }}</component>
                    <p v-if="text" :class="textClasses">{{ text.trim() }}</p>
                </div>

                <div v-if="hasButtons" :class="buttonRowClasses">
                    <Button
                        v-for="(buttonData, index) in visibleButtons"
                        :key="`button-${index}`"
                        v-bind="buttonData.props"
                        :classes="buttonData.classes"
                        class="w-auto" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { vercelStegaCleanAll } from '@sanity/client/stega';
    interface ButtonConfig {
        hidden?: boolean;
        button: {
            title?: string;
            type?: string;
            internalLink?: { _ref: string };
            externalUrl?: string;
        };
    }

    interface ImageObject {
        alt?: string;
        [key: string]: any;
    }

    const props = defineProps<{
        image?: ImageObject;
        imageMobile?: ImageObject;
        title?: string;
        text?: string;
        button1?: ButtonConfig;
        button2?: ButtonConfig;
        // `<vertical>-<horizontal>` from the Sanity alignment picker (e.g. 'top-left');
        // legacy documents still hold 'left' | 'center' | 'right'
        textPosition?: string;
        textPositionMobile?: string;
        // 'left' | 'center' | 'right'; unset = follow the text position
        textAlignment?: string;
        textAlignmentMobile?: string;
        textColorDesktop?: string;
        textColorMobile?: string;
        buttonTypeMobile?: string;
        shouldDisplayBehindNavigationOnDesktop?: boolean;
        textStyle?: 'small' | 'big';
        blog?: boolean;
        titleLevel?: string;
    }>();

    const { getUrlById } = useCategorySeoUrl();

    const createImageUrl = (image: ImageObject, width: number): string => {
        return useImageUrl(image).format('webp').quality(100).width(width).url();
    };

    const isColorWhite = (color?: string): boolean => vercelStegaCleanAll(color) === '#FFFFFF';
    const isColorBlack = (color?: string): boolean => vercelStegaCleanAll(color) === '#000000';

    // Text position values are `<vertical>-<horizontal>`; legacy values only carried the horizontal part
    const legacyPositions: Record<string, string> = {
        left: 'center-left',
        center: 'center-center',
        right: 'center-right',
    };
    const parseAlignment = (raw: string | undefined, fallback: string) => {
        const cleaned = vercelStegaCleanAll(raw) || fallback;
        const [vertical, horizontal] = (legacyPositions[cleaned] || cleaned).split('-');
        const [fallbackVertical, fallbackHorizontal] = fallback.split('-');
        return {
            vertical: ['top', 'center', 'bottom'].includes(vertical) ? vertical : fallbackVertical,
            horizontal: ['left', 'center', 'right'].includes(horizontal) ? horizontal : fallbackHorizontal,
        };
    };
    const alignment = computed(() => parseAlignment(props.textPosition, 'center-left'));
    const alignmentMobile = computed(() => parseAlignment(props.textPositionMobile, 'bottom-center'));

    // Alignment of the copy itself; falls back on the horizontal part of the text position
    const parseTextAlign = (raw: string | undefined, fallback: string) => {
        const cleaned = vercelStegaCleanAll(raw);
        return ['left', 'center', 'right'].includes(cleaned) ? cleaned : fallback;
    };
    const textAlign = computed(() => parseTextAlign(props.textAlignment, alignment.value.horizontal));
    const textAlignMobile = computed(() =>
        parseTextAlign(props.textAlignmentMobile, alignmentMobile.value.horizontal),
    );

    const titleWithBreak = (value?: string): string => {
        if (!value) return '';
        const words = value.split(' ');
        if (words.length > 3) {
            const firstThree = words.slice(0, 3).join(' ');
            const remaining = words.slice(3).join(' ');
            return `${firstThree}\n${remaining}`;
        }
        return value;
    };

    // Style computations
    const bannerClasses = computed(() => [
        'header-banner relative flex w-full items-center justify-center overflow-hidden xl:-mt-[62px]',
        { 'mt-0 xl:!-mt-[110px]': props.shouldDisplayBehindNavigationOnDesktop },
    ]);

    // Vertical alignment per breakpoint. Top/bottom get extra spacing so the text
    // clears the (overlaying) navigation and the edge.
    const verticalClassesMobile: Record<string, string> = {
        top: 'items-start mt-10',
        center: 'items-center',
        bottom: 'items-end mb-10',
    };
    const verticalClassesDesktop: Record<string, string> = {
        top: 'lg:mt-0 lg:mb-0 lg:items-start lg:pt-32',
        center: 'lg:mt-0 lg:mb-0 lg:items-center',
        bottom: 'lg:mt-0 lg:mb-0 lg:items-end lg:pb-14',
    };

    const contentOverlayClasses = computed(() => {
        const baseClasses =
            'normal-container absolute inset-0 mx-[20px] grid grid-cols-1 justify-center lg:mx-auto lg:grid-cols-2 lg:justify-center';
        return [
            baseClasses,
            verticalClassesMobile[alignmentMobile.value.vertical],
            verticalClassesDesktop[alignment.value.vertical],
            {
                'lg:text-white': isColorWhite(props.textColorDesktop),
                'lg:text-black': isColorBlack(props.textColorDesktop),
                'text-white': isColorWhite(props.textColorMobile),
                'text-black': isColorBlack(props.textColorMobile),
            },
        ];
    });

    // The text block's grid column follows the horizontal position; the text-align
    // follows the (possibly overridden) text alignment
    const columnDesktop: Record<string, string> = {
        left: 'lg:!col-start-1',
        center: 'lg:!col-span-2',
        right: 'lg:!col-start-2',
    };
    const textAlignClassesMobile: Record<string, string> = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    };
    const textAlignClassesDesktop: Record<string, string> = {
        left: 'lg:text-left',
        center: 'lg:text-center',
        right: 'lg:text-right',
    };
    const textContainerClasses = computed(() => [
        textAlignClassesMobile[textAlignMobile.value],
        columnDesktop[alignment.value.horizontal],
        textAlignClassesDesktop[textAlign.value],
    ]);

    const justifyMobile: Record<string, string> = {
        left: 'justify-start',
        center: 'justify-center',
        right: 'justify-end',
    };
    const justifyDesktop: Record<string, string> = {
        left: 'lg:justify-start',
        center: 'lg:justify-center',
        right: 'lg:justify-end',
    };
    const buttonRowClasses = computed(() => [
        'mt-6 flex flex-row items-center gap-4',
        justifyMobile[alignmentMobile.value.horizontal],
        justifyDesktop[alignment.value.horizontal],
    ]);

    const titleTag = computed(() => (props.blog ? 'h1' : props.titleLevel || 'h2'));

    const titleClasses =
        'h1 whitespace-pre-wrap !text-[24px] leading-[32px] drop-shadow md:!text-4xl md:leading-[48px] lg:drop-shadow-none';

    // The paragraph is a constrained box; the horizontal alignment pins it to the matching edge
    const edgeMobile: Record<string, string> = {
        left: 'ml-0 mr-auto',
        center: 'mx-auto',
        right: 'ml-auto mr-0',
    };
    const edgeDesktop: Record<string, string> = {
        left: 'lg:ml-0 lg:mr-auto',
        center: 'lg:mx-auto',
        right: 'lg:ml-auto lg:mr-0',
    };
    const textClasses = computed(() => {
        const baseClasses = 'drop-shadow lg:drop-shadow-none font-medium';
        const edgeClasses = `${edgeMobile[alignmentMobile.value.horizontal]} ${edgeDesktop[alignment.value.horizontal]}`;
        return props.textStyle === 'big'
            ? `${baseClasses} text-[14px] w-[90%] lg:w-auto ${edgeClasses} lg:text-2xl mt-4 font-monument-extended lg:leading-10`
            : `${baseClasses} text-sm mt-4 font-mori-regular w-4/5 md:w-3/5 ${edgeClasses} lg:text-base`;
    });

    // Image computations
    const primaryImageUrl = computed(() => {
        if (props.image) return createImageUrl(props.image, 2400);
        if (props.imageMobile) return createImageUrl(props.imageMobile, 1024);
        return '';
    });

    const mobileImageUrl = computed(() => (props.imageMobile ? createImageUrl(props.imageMobile, 1024) : ''));

    const imageProps = {
        placeholder: false,
        fetchpriority: 'high' as const,
        decoding: 'async' as const,
        loading: 'eager' as const,
        format: 'webp',
        width: '2400',
        height: '960',
    };

    // Button utilities
    const shouldShowButton = (buttonConfig?: ButtonConfig): boolean => {
        return !!(buttonConfig && !buttonConfig.hidden && buttonConfig.button?.title?.trim());
    };

    const getButtonProps = (buttonConfig: ButtonConfig, url: string) => ({
        title: buttonConfig.button.title,
        type: buttonConfig.button.type,
        buttonType: buttonConfig.button.type || 'internal',
        link: url,
        buttonTypeMobile: props.buttonTypeMobile,
    });

    const getButtonClasses = (isFirstButton: boolean): string => {
        const commonClasses = '!py-[14px] !px-10 !w-max !text-[14px]';

        if (props.textStyle === 'small') {
            const fontWeight = isFirstButton ? '!font-semibold lg:!font-normal' : '!font-normal';
            return `${fontWeight} ${commonClasses} lg:!text-[14px]`;
        }

        const spacing = isFirstButton ? 'lg:mr-0' : 'ml-2 lg:ml-0';
        return `!py-[12px] ${commonClasses} lg:!text-[18px] ${spacing}`;
    };

    const visibleButtons = computed(() => {
        const buttons = [];

        if (shouldShowButton(props.button1) && props.button1) {
            buttons.push({
                props: getButtonProps(props.button1, button1Url.value),
                classes: getButtonClasses(true),
            });
        }

        if (shouldShowButton(props.button2) && props.button2) {
            buttons.push({
                props: getButtonProps(props.button2, button2Url.value),
                classes: getButtonClasses(buttons.length === 0), // isFirstButton if no previous buttons
            });
        }

        return buttons;
    });

    const hasButtons = computed(() => visibleButtons.value.length > 0);

    const getButtonUrl = async (button?: ButtonConfig['button']): Promise<string> => {
        if (!button) return '#';

        if (button?.buttonType === 'internal' && button.title) {
            try {
                const seoPathInfo = getUrlById(button.internalLink._ref);
                return seoPathInfo ? seoPathInfo.toLowerCase() : '#';
            } catch (error) {
                console.error('Error fetching SEO URL:', error);
                return '#';
            }
        }

        return button.externalUrl || '#';
    };

    // Resolve button URLs with SSR/client hydration support
    const cacheKey = `header-banner-urls-${JSON.stringify([
        props.button1?.button?.internalLink?._ref,
        props.button2?.button?.internalLink?._ref,
    ])}`;

    const { data: buttonUrls } = await useAsyncData(cacheKey, async () => {
        const [button1UrlResult, button2UrlResult] = await Promise.all([
            getButtonUrl(props.button1?.button),
            getButtonUrl(props.button2?.button),
        ]);
        return {
            button1: button1UrlResult,
            button2: button2UrlResult,
        };
    });

    const button1Url = computed(() => buttonUrls.value?.button1 || '#');
    const button2Url = computed(() => buttonUrls.value?.button2 || '#');

    // Image preloading
    const createPreloadLink = (href: string, media: string) => ({
        rel: 'preload',
        as: 'image' as const,
        href,
        media,
        crossorigin: 'anonymous' as const,
        fetchpriority: 'high' as const,
    });

    // Preload on both server and client to prevent flickering
    const preloadLinks = [
        ...(props.image ? [createPreloadLink(primaryImageUrl.value, '(min-width: 1024px)')] : []),
        ...(props.imageMobile ? [createPreloadLink(mobileImageUrl.value, '(max-width: 1023px)')] : []),
    ];

    if (preloadLinks.length > 0) {
        useHead({ link: preloadLinks });
    }
</script>
